import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	VictoryAxis,
	VictoryChart,
	VictoryLegend,
	VictoryLine,
	VictoryScatter,
} from 'victory';
import dayjs from 'dayjs';
import Utils from 'common/services/Utils';
import { DEFAULT_LANGUAGE, STORAGE } from 'Config';
import ChartLegendLabel from 'common/components/charts/ChartLegendLabel';
import Colors from 'styles/export/colors.module.scss';
import useElementSize from 'common/hooks/useElementSize';
import Storage from 'common/services/Storage';
import {
	FuelConsumptionKind,
	FuelConsumptionPerAssetDashboardDto,
	FuelConsumptionPerAssetMonthDto,
} from 'api/dashboard/dtos/FuelConsumptionPerAssetDashboardDto';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import WidgetNoData from '../components/WidgetNoData';
import { formatConsumptionNumber, getConsumptionUnitKey } from './fuelConsumptionUtils';
import styles from './FuelConsumptionPerAssetChart.module.scss';

// Series palette per docs/COLORS.md (data colors 1–5).
const SERIES_PALETTE = ['#02c7c7', '#038cb6', '#7c5cbf', '#e8873d', '#e05a8a'];

const colorForIndex = (index: number) => SERIES_PALETTE[index % SERIES_PALETTE.length];

type Props = {
	data: FuelConsumptionPerAssetDashboardDto;
	fuelKind: FuelConsumptionKind;
};

const FuelConsumptionPerAssetChart = ({ data, fuelKind }: Props) => {
	const { t } = useTranslation();
	const [hiddenVehicles, setHiddenVehicles] = useState<string[]>([]);
	const [reRenderKey, setReRenderKey] = useState(new Date().getTime());
	const [containerRef, { width, height }] = useElementSize();
	const [tooltip, setTooltip] = useState<{ dateTime: number; clientX: number; clientY: number; vehicleId: string } | null>(null);
	const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;
	const unit = t(getConsumptionUnitKey(fuelKind) as any) as string;

	useEffect(() => {
		setReRenderKey(new Date().getTime());
	}, [data, width, height]);

	if (!data.items || data.items.length === 0) {
		return <WidgetNoData />;
	}

	const chartWidth = Math.max(width, 100);
	const legendHeight = 28;
	const chartHeight = Math.max(height, 60);
	const chartPadding = { top: 15, left: 65, right: 25, bottom: 35 + legendHeight };

	const visibleItems = data.items.filter(v => !hiddenVehicles.includes(v.vehicleId));

	const allDates = data.items
		.flatMap(v => v.items)
		.map(x => x.date)
		.filter((date, i, self) => self.findIndex(d => d.getTime() === date.getTime()) === i)
		.sort((a, b) => a.getTime() - b.getTime());

	const allValues: number[] = [];
	visibleItems.forEach(v => v.items.forEach(m => {
		const num = m.value == null ? null : Number(m.value);
		if (num != null && !isNaN(num)) allValues.push(num);
	}));
	visibleItems.forEach(v => {
		const num = v.referenceConsumption == null ? null : Number(v.referenceConsumption);
		if (num != null && !isNaN(num)) allValues.push(num);
	});
	const highestValue = allValues.length > 0 ? Math.max(...allValues) : 0;

	// Eixo Y com passos "bonitos" (1, 2, 5 × potência de 10) para nunca cair em decimais que
	// arredondam todos para 0 no formatador. Garante 4 ticks distintos entre 0 e o topo.
	const niceStep = (range: number, ticks: number) => {
		if (range <= 0) return 1;
		const rough = range / ticks;
		const exp = Math.pow(10, Math.floor(Math.log10(rough)));
		const rel = rough / exp;
		const stepRel = rel < 1.5 ? 1 : rel < 3 ? 2 : rel < 7 ? 5 : 10;
		return stepRel * exp;
	};
	const TARGET_TICKS = 4;
	const step = niceStep(highestValue * 1.1, TARGET_TICKS);
	const yMax = Math.max(step, Math.ceil((highestValue * 1.1) / step) * step);
	const yTickValues: number[] = [];
	for (let v = 0; v <= yMax + step / 2; v += step) yTickValues.push(Number(v.toFixed(6)));

	// Para o tooltip mostrar todos os ativos no mês onde o utilizador está em hover.
	const toMonthKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}`;
	const monthBreakdown = new Map<string, { rows: { vehicleId: string; registrationNumber: string; value: number | null; reference: number | null; color: string }[] }>();
	allDates.forEach(date => {
		const key = toMonthKey(date);
		if (monthBreakdown.has(key)) return;
		const rows = data.items.map((v, i) => ({
			vehicleId: v.vehicleId,
			registrationNumber: v.registrationNumber,
			value: v.items.find((m: FuelConsumptionPerAssetMonthDto) => toMonthKey(m.date) === key)?.value ?? null,
			reference: v.referenceConsumption ?? null,
			color: colorForIndex(i),
		}));
		monthBreakdown.set(key, { rows });
	});

	const legendData = data.items.map((v, i) => ({
		name: v.registrationNumber,
		vehicleId: v.vehicleId,
		symbol: { fill: !hiddenVehicles.includes(v.vehicleId) ? colorForIndex(i) : Colors.gray17 },
		labels: { fill: Colors.primaryDark },
		clickable: true,
	}));

	const onClickLegend = (propsData: any) => {
		const vehicleId = propsData.datum.vehicleId;
		if (!vehicleId) return;
		setHiddenVehicles(prev => prev.includes(vehicleId) ? prev.filter(x => x !== vehicleId) : [...prev, vehicleId]);
		setReRenderKey(new Date().getTime());
	};

	const legendItemWidth = 130;
	const legendTotalWidth = legendData.length * legendItemWidth;
	const legendX = Math.max((chartWidth - legendTotalWidth) / 2, 0);

	const tooltipData = tooltip ? monthBreakdown.get(toMonthKey(new Date(tooltip.dateTime))) : null;
	const hoveredVehicleId = tooltip?.vehicleId ?? null;
	const hoveredVehicle = hoveredVehicleId ? data.items.find(v => v.vehicleId === hoveredVehicleId) : null;
	const hoveredColor = hoveredVehicleId
		? colorForIndex(data.items.findIndex(v => v.vehicleId === hoveredVehicleId))
		: null;

	return (
		<div className={styles.chartContainer} ref={containerRef}>
			{width > 0 && height > 0 && (
				<svg width={width} height={height} key={'svg-' + reRenderKey}>
					{visibleItems.length > 0 && (
						<VictoryChart
							standalone={false}
							width={chartWidth}
							height={chartHeight}
							padding={chartPadding}
							scale={{ x: 'time' }}
							domainPadding={{ x: 30, y: 0 }}
							domain={{ y: [0, yMax] }}
							key={'chart-' + reRenderKey}
						>
							{/* Linhas reais por ativo. Pontos com value=null são filtrados para criarem gaps na linha. */}
							{visibleItems.map(vehicle => {
								const colorIndex = data.items.findIndex(v => v.vehicleId === vehicle.vehicleId);
								const color = colorForIndex(colorIndex);
								const points = vehicle.items.filter(d => d.value != null);
								if (points.length === 0) return null;
								return (
									<VictoryLine
										key={'line-' + vehicle.vehicleId}
										data={points}
										x="date"
										y="value"
										interpolation="monotoneX"
										style={{ data: { stroke: color, strokeWidth: 2 } }}
									/>
								);
							})}

							{/* Linha tracejada de referência: só aparece para o ativo em hover. */}
							{hoveredVehicle && hoveredVehicle.referenceConsumption != null && hoveredColor && allDates.length > 0 && (
								<VictoryLine
									key={'ref-' + hoveredVehicle.vehicleId + '-' + reRenderKey}
									data={[
										{ date: allDates[0], value: hoveredVehicle.referenceConsumption },
										{ date: allDates[allDates.length - 1], value: hoveredVehicle.referenceConsumption },
									]}
									x="date"
									y="value"
									style={{ data: { stroke: hoveredColor, strokeWidth: 1.5, strokeDasharray: '6,4' } }}
								/>
							)}

							{/* Pontos clicáveis e responsáveis pelo hover. */}
							{visibleItems.map(vehicle => {
								const colorIndex = data.items.findIndex(v => v.vehicleId === vehicle.vehicleId);
								const color = colorForIndex(colorIndex);
								const events: any[] = [
									{
										target: 'data',
										eventHandlers: {
											onMouseEnter: (evt: any, targetProps: any) => {
												setTooltip({
													dateTime: targetProps.datum.date.getTime(),
													clientX: evt.clientX,
													clientY: evt.clientY,
													vehicleId: vehicle.vehicleId,
												});
												return [];
											},
											onMouseMove: (evt: any) => {
												setTooltip(prev => prev ? { ...prev, clientX: evt.clientX, clientY: evt.clientY } : null);
												return [];
											},
											onMouseLeave: () => {
												setTooltip(null);
												return [];
											},
										},
									},
								];
								return (
									<VictoryScatter
										key={'scatter-' + vehicle.vehicleId}
										data={vehicle.items.filter(d => d.value != null)}
										x="date"
										y="value"
										size={4}
										style={{ data: { fill: color, cursor: 'pointer' } }}
										events={events}
									/>
								);
							})}
							<VictoryAxis
								dependentAxis
								tickValues={yTickValues}
								style={{
									axis: { stroke: 'transparent' },
									grid: { stroke: '#f5f5f5' },
									tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
								}}
								// Só o número nos ticks Y. A unidade fica fora do gráfico
								// (KpiHeader + tooltips) para não cortar os labels com o padding esquerdo.
								tickFormat={x => formatConsumptionNumber(Number(x))}
							/>
							<VictoryAxis
								orientation="bottom"
								width={chartWidth}
								height={chartHeight}
								tickValues={allDates}
								tickFormat={x => Utils.capitalize(dayjs(x).locale(locale).format('MMM')) + ' ' + dayjs(x).format('YY')}
								style={{
									tickLabels: { fill: Colors.black, userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' },
									axis: { stroke: Colors.gray3 },
								}}
							/>
						</VictoryChart>
					)}

					<VictoryLegend
						standalone={false}
						x={legendX}
						y={chartHeight - legendHeight}
						gutter={40}
						data={legendData}
						orientation={'horizontal'}
						itemsPerRow={Math.max(legendData.length, 1)}
						labelComponent={<ChartLegendLabel onClick={onClickLegend} />}
						style={{ labels: { fontSize: 12, userSelect: 'none', fontFamily: 'Roboto' } }}
					/>
				</svg>
			)}

			{tooltip && tooltipData && (
				<ChartTooltipPortal clientX={tooltip.clientX} clientY={tooltip.clientY} className={styles.tooltip}>
					{tooltipData.rows.map(row => {
						const isHovered = row.vehicleId === hoveredVehicleId;
						const valueText = row.value != null ? `${formatConsumptionNumber(row.value)} ${unit}` : '–';
						const showReference = isHovered && row.reference != null;
						const delta = showReference && row.value != null ? row.value - (row.reference as number) : null;
						const deltaClass = delta == null
							? ''
							: delta > 0 ? styles.tooltipDeltaPositive : styles.tooltipDeltaNegative;
						return (
							<React.Fragment key={row.vehicleId}>
								<div className={styles.tooltipRow}>
									<span className={styles.tooltipDot} style={{ backgroundColor: row.color }} />
									<span>{row.registrationNumber}: {valueText}</span>
								</div>
								{showReference && (
									<div className={styles.tooltipReference}>
										<span>{t('dashboard.consumption_reference_label' as any) as string}: {formatConsumptionNumber(row.reference)} {unit}</span>
										{delta != null && (
											<span className={deltaClass}>
												({delta > 0 ? '+' : ''}{formatConsumptionNumber(delta)})
											</span>
										)}
									</div>
								)}
							</React.Fragment>
						);
					})}
				</ChartTooltipPortal>
			)}
		</div>
	);
};

export default FuelConsumptionPerAssetChart;
